import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "-- Select --",
  className = "",
  disabled = false,
  error = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? "";

  const filtered = search
    ? options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setSearch("");
    setHighlightIndex(-1);
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    setHighlightIndex(-1);
  }, []);

  const select = useCallback(
    (val: string) => {
      onChange(val);
      close();
    },
    [onChange, close]
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightIndex >= 0 && filtered[highlightIndex]) {
          select(filtered[highlightIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
    }
  };

  const errorClass = error ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-500/20" : "";

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger / display */}
      {!isOpen ? (
        <button
          type="button"
          onClick={open}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          className={`form-input w-full text-left flex items-center justify-between ${errorClass} ${
            disabled ? "opacity-50 bg-gray-100" : ""
          } ${!value ? "text-gray-500" : ""}`}
        >
          <span className="truncate">{value ? selectedLabel : placeholder}</span>
          <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
        </button>
      ) : (
        <div className={`form-input flex items-center gap-2 ring-2 ring-brand-primary/20 border-brand-primary ${errorClass}`}>
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setHighlightIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={`Search${selectedLabel ? ` (${selectedLabel})` : ""}...`}
            className="flex-1 outline-none bg-transparent text-sm"
            autoFocus
          />
          <ChevronDown size={16} className="text-gray-400 flex-shrink-0 rotate-180" />
        </div>
      )}

      {/* Dropdown list */}
      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          role="listbox"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-sm text-gray-500">No results found</li>
          ) : (
            filtered.map((option, idx) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(option.value);
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                  idx === highlightIndex
                    ? "bg-brand-primary/10 text-brand-primary"
                    : option.value === value
                      ? "bg-gray-50 font-medium"
                      : "hover:bg-gray-50"
                }`}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
